/**
 * executiveApi.js — API functions for the Executive Report page.
 *
 * fetchExecutiveReport       — runs the executive report query (yesterday)
 * fetchExecutiveFilterOptions — loads all filter option datasets
 * saveExecThreshold          — persists excessive response threshold to campus config
 */

import { apiFetch, apiSave } from '../../../../services/api.js';
import { getAuthHeaders } from '../../../../services/auth';

const getBaseUrl = () =>
    import.meta.env.VITE_DEVMODE === 'true'
        ? import.meta.env.VITE_DEVAPI
        : import.meta.env.VITE_API;

const getHeaders = () => getAuthHeaders();

// ---------------------------------------------------------------------------
// Fetch Executive Report
// ---------------------------------------------------------------------------

/**
 * @param {object} opts
 * @param {number} opts.campusId
 * @param {string} opts.username
 * @param {string} opts.startDate — YYYY-MM-DD
 * @param {string} opts.endDate   — YYYY-MM-DD
 * @param {object} opts.filters
 * @param {number} opts.execTime  — excessive response threshold in minutes
 * @returns {Promise<object>}
 */
export async function fetchExecutiveReport({ campusId, username, startDate, endDate, filters = {}, execTime = 20 }) {
    const body = {
        rep_type: 'executiveReport',
        username: username || '',
        campus_id: campusId,
        start_date: startDate,
        end_date: endDate,
        event_category_filter: '1',
        device_category_filter: filters.deviceCategories || '',
        area_type_filter: filters.areaTypes || '',
        reason_filter: filters.reasons || '',
        user_filter: filters.users || '',
        event_type_filter: filters.eventTypes || '',
        resident_filter: filters.residents || '',
        resident_group_filter: filters.residentGroups || '',
        area_filter: filters.areas || '',
        area_group_filter: filters.areaGroups || '',
        user_group_filter: filters.userGroups || '',
        excessive_filter: execTime,
        items: {
            weekly_residents: 0,
            weekly_caregivers: 0,
            excessive_alerts: 1,
            daily_areas: 1,
            daily_area_types: 0,
            reason_data: 1,
            area_types_response: 1,
            area_types_response_chart: 1,
        },
    };

    try {
        const resp = await fetch(getBaseUrl() + import.meta.env.VITE_MY_REPORTS_ENDPOINT, {
            method: 'POST',
            headers: await getHeaders(),
            body: JSON.stringify(body),
        });

        if (resp.status === 503) return { ErrorCode: 11 };
        if (resp.status === 500) return { ErrorCode: 10 };
        if (resp.status === 403) return { ErrorCode: 12 };

        const raw = await resp.json();
        if (raw?.ErrorCode) return { ErrorCode: raw.ErrorCode };

        const data = raw?.Res || raw;
        if (data?.ErrorCode) return { ErrorCode: data.ErrorCode };

        return {
            excessiveAlerts: data?.excessive_alerts || [],
            reasonData: data?.reason_data || {},
            areaTypesResponse: data?.area_types_response || [],
            areaTypesResponseChart: data?.area_types_response_chart || {},
            dailyAreas: data?.daily_areas || [],
        };
    } catch (err) {
        console.error('[executiveApi] fetchExecutiveReport error:', err);
        return { ErrorCode: 10 };
    }
}

// ---------------------------------------------------------------------------
// Fetch Filter Options (same as AlertDetails — full set)
// ---------------------------------------------------------------------------

export async function fetchExecutiveFilterOptions(campusId) {
    try {
        const [portalFilters, eventTypes, reasonCodes, devices] = await Promise.all([
            fetchPortalFilters(campusId),
            apiFetch({ table: 'att_event_types', procedure: 'fetchall', reqType: 'direct', pKey: 'campus_id', pVal: campusId }),
            apiFetch({ table: 'core_reason_codes', procedure: 'fetchbyparameter', reqType: 'direct', pKey: 'campus_id', pVal: campusId }),
            apiFetch({ procedure: 'sp_dev_getInovonicsDevices', reqType: 'stored', payload: { device_filter: '', device_category_filter: '1,2,3,6,7,8,9,10,11,12,15,16,17', campus_id: campusId } }),
        ]);

        const reasonsWithNoReason = [
            { reason_code_id: 0, reason_code_description: 'No Reason' },
            ...(reasonCodes || []),
        ];

        const residentsWithName = (portalFilters.residents || []).map((r) => ({
            ...r,
            resident_name: `${r.resident_first_name || ''} ${r.resident_last_name || ''}`.trim() || '(unknown)',
        }));

        return {
            areas: portalFilters.areas || [],
            areaGroups: portalFilters.areaGroups || [],
            areaTypes: portalFilters.areaTypes || [],
            residents: residentsWithName,
            residentGroups: portalFilters.residentGroups || [],
            users: portalFilters.users || [],
            userGroups: portalFilters.userGroups || [],
            deviceCategories: portalFilters.deviceCategories || [],
            devices: devices || [],
            eventTypes: eventTypes || [],
            reasonCodes: reasonsWithNoReason,
        };
    } catch (err) {
        console.error('[executiveApi] fetchExecutiveFilterOptions error:', err);
        return {};
    }
}

async function fetchPortalFilters(campusId) {
    const body = {
        rep_type: 'getportalfilters',
        campus_id: campusId,
        device_category_filter: '1',
        area_type_filter: '1',
        user_filter: '1',
        resident_group_filter: '1',
        area_filter: '0',
        area_group_filter: '1',
        user_group_filter: '1',
        resident_filter: '1',
    };
    try {
        const resp = await fetch(getBaseUrl() + import.meta.env.VITE_MY_REPORTS_ENDPOINT, {
            method: 'POST',
            headers: await getHeaders(),
            body: JSON.stringify(body),
        });
        const raw = await resp.json();
        const data = raw?.Res || raw;
        return {
            areas: data?.areas || [],
            areaGroups: data?.area_groups || [],
            areaTypes: data?.area_types || [],
            residents: data?.residents || [],
            residentGroups: data?.resident_groups || [],
            users: data?.users || [],
            userGroups: data?.user_groups || [],
            deviceCategories: data?.device_categories || [],
        };
    } catch (err) {
        console.error('[executiveApi] fetchPortalFilters error:', err);
        return {};
    }
}

// ---------------------------------------------------------------------------
// Save Excessive Response Threshold
// ---------------------------------------------------------------------------

/**
 * @param {number} campusId
 * @param {number} execTime — new threshold in minutes
 * @param {number} oldExecTime — previous threshold
 * @param {string} userId
 * @param {string} clientId
 * @returns {Promise<[boolean, string?]>}
 */
export async function saveExecThreshold(campusId, execTime, oldExecTime, userId, clientId) {
    return apiSave({
        table: 'core_campus',
        payload: [{ campus_id: campusId, exec_excess_time: execTime, modified_by: userId }],
        oldPayload: [{ campus_id: campusId, exec_excess_time: oldExecTime }],
        pKey: 'campus_id',
        condition: 'primary',
        method: 'POST',
        campusId,
        clientId,
        userId,
    });
}
